import * as React from 'react';
import type { ReactNode, FormHTMLAttributes } from 'react';
import { cx } from '../utils';
import type { StyledProps } from '../types';
import type { UnsafeStyleProps } from '../escape-hatches';
import { FormDensityContext, type Density } from '../density';

/* Bounded rest-prop allow-list typed via React.FormHTMLAttributes
   (SectionHeader r1 lesson — avoids unknown-type collisions). Form
   accepts the full set of native <form> attrs adopters expect. */
type AllowedRest = Pick<
  FormHTMLAttributes<HTMLFormElement>,
  | 'id'
  | 'title'
  | 'lang'
  | 'dir'
  | 'action'
  | 'method'
  | 'name'
  | 'target'
  | 'noValidate'
  | 'autoComplete'
  | 'encType'
  | 'acceptCharset'
> & {
  [key: `data-${string}`]: string | number | boolean | undefined;
  [key: `aria-${string}`]: string | boolean | number | undefined;
};
const ALLOWED_LITERAL_KEYS = new Set<string>([
  'id',
  'title',
  'lang',
  'dir',
  'action',
  'method',
  'name',
  'target',
  'noValidate',
  'autoComplete',
  'encType',
  'acceptCharset',
]);
function pickAllowed(rest: Record<string, unknown>): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const key of Object.keys(rest)) {
    if (ALLOWED_LITERAL_KEYS.has(key) || key.startsWith('data-') || key.startsWith('aria-')) {
      out[key] = rest[key];
    }
  }
  return out;
}

export interface FormProps extends StyledProps, UnsafeStyleProps, AllowedRest {
  children?: ReactNode;
  onSubmit?: React.FormEventHandler<HTMLFormElement>;
  /* When set, opens a FormDensityContext.Provider so nested Field
     controls reading useFormDensity() see this value. Adopters who
     want per-field density overrides pass `density` on the
     FormField instead. */
  density?: Density;
}

export const Form = React.forwardRef<HTMLFormElement, FormProps>(function Form(
  { children, onSubmit, density, className, unsafe_style, ...rest },
  ref,
) {
  const restProps = pickAllowed(
    rest as Record<string, unknown>,
  ) as React.FormHTMLAttributes<HTMLFormElement>;

  const form = (
    <form
      ref={ref}
      {...restProps}
      className={cx('j-form', className)}
      style={unsafe_style}
      onSubmit={onSubmit}
    >
      {children}
    </form>
  );

  if (density) {
    return <FormDensityContext.Provider value={density}>{form}</FormDensityContext.Provider>;
  }
  return form;
});
